// Constants
export const KG_CO2_PER_GAL_GAS = 8.887;
export const GAS_ESCALATION_YOY = 0.039;
export const ELEC_ESCALATION_YOY = 0.025;

export interface FleetFormData {
  address: string;
  city: string;
  zipCode: string;
  disableUtilityInfo?: boolean;
  fleetSize: string;
  milesPerYear: string;
  iceMpg: string;
  fuelPrice: string;
  iceMaint: string;
  evMaint: string;
  milesPerKwh: string;
  elecPrice: string;
  evPrice: string;
  incentive: string;
  resalePerVeh: string;
  infraTotal: string;
  years: string;
  includeGridEmissions: boolean;
  gridIntensity: string;
  applyTaxBenefits: boolean;
  businessTaxRate: string;
  section179Limit: string;
  bonusDepreciationPct: string;
  qualifyingSpendOverride: string;
  disableUtilityEnergyInfo: boolean;
}

export interface FleetReportData {
  address: string;
  city: string;
  zipCode: string;
  utilityRate: number;
  iceFuelCostAnnual: number;
  evEnergyCostAnnual: number;
  iceMaintAnnual: number;
  evMaintAnnual: number;
  iceOpexAnnual: number;
  evOpexAnnual: number;
  annualSavings: number;
  upfrontEVs: number;
  incentives: number;
  resaleTotal: number;
  upfrontGross: number;
  upfrontAfterTax: number;
  upfrontNet: number;
  applyTaxBenefits: boolean;
  businessTaxRate: number;
  section179Limit: number;
  bonusDepreciationPct: number;
  qualifyingSpend: number;
  est179Deduction: number;
  est168kDeduction: number;
  estTaxSavings: number;
  paybackYears: number;
  totalSavings: number;
  netSavings: number;
  gallonsAvoidedAnnual: number;
  co2AnnualKgTailpipe: number;
  gridEmissionsAnnual: number;
  co2AnnualKgNet: number;
  co2NetTons: number;
  costOverTimeIce: number[];
  costOverTimeEv: number[];
  cumulativeNetSavings: number[];
  yearsArray: number[];
}

export const calculateFleetReport = (
  formData: FleetFormData,
  utilityRate: number = 0.14
): FleetReportData => {
  const fleetSize = parseFloat(formData.fleetSize) || 1;
  const milesPerYear = parseFloat(formData.milesPerYear) || 0;
  const iceMpg = parseFloat(formData.iceMpg) || 1;
  const fuelPrice = parseFloat(formData.fuelPrice) || 0;
  const iceMaint = parseFloat(formData.iceMaint) || 0;
  const evMaint = parseFloat(formData.evMaint) || 0;
  const milesPerKwh = parseFloat(formData.milesPerKwh) || 1;
  const elecPrice = parseFloat(formData.elecPrice) || utilityRate;
  const evPrice = parseFloat(formData.evPrice) || 0;
  const incentive = parseFloat(formData.incentive) || 0;
  const resalePerVeh = parseFloat(formData.resalePerVeh) || 0;
  const infraTotal = parseFloat(formData.infraTotal) || 0;
  const years = parseInt(formData.years) || 7;
  const gridIntensity = parseFloat(formData.gridIntensity) || 0.4;
  const includeGridEmissions = formData.includeGridEmissions;

  // Tax calculation inputs
  const applyTaxBenefits = formData.applyTaxBenefits;
  const businessTaxRate = parseFloat(formData.businessTaxRate) || 21;
  const section179Limit = parseFloat(formData.section179Limit) || 2500000;
  const bonusDepreciationPct =
    Math.max(0, Math.min(100, parseFloat(formData.bonusDepreciationPct) || 0)) /
    100;
  const qualOverride = parseFloat(formData.qualifyingSpendOverride) || 0;

  // Core calculations
  const gallonsPerVehPerYr = milesPerYear / Math.max(iceMpg, 0.0001);
  const kwhPerVehPerYr = milesPerYear / Math.max(milesPerKwh, 0.0001);

  const iceFuelCostAnnual = gallonsPerVehPerYr * fuelPrice * fleetSize;
  const evEnergyCostAnnual = kwhPerVehPerYr * elecPrice * fleetSize;

  const iceMaintAnnual = iceMaint * fleetSize;
  const evMaintAnnual = evMaint * fleetSize;

  const iceOpexAnnual = iceFuelCostAnnual + iceMaintAnnual;
  const evOpexAnnual = evEnergyCostAnnual + evMaintAnnual;

  const annualSavings = iceOpexAnnual - evOpexAnnual;

  // Upfront costs
  const upfrontEVs = evPrice * fleetSize;
  const incentives = incentive * fleetSize;
  const resaleTotal = resalePerVeh * fleetSize;
  const upfrontGross = Math.max(
    0,
    upfrontEVs - incentives - resaleTotal + infraTotal
  );

  // Tax calculations
  let est179Deduction = 0;
  let est168kDeduction = 0;
  let estTaxSavings = 0;
  let qualifyingSpend = 0;

  if (applyTaxBenefits) {
    const taxRate = Math.max(0, Math.min(1, businessTaxRate / 100));

    // Qualifying spend default: EVs + infrastructure
    const defaultQualSpend = upfrontEVs + infraTotal;
    qualifyingSpend = qualOverride > 0 ? qualOverride : defaultQualSpend;

    const base179 = Math.min(qualifyingSpend, section179Limit);
    est179Deduction = base179;

    const bonusBase = Math.max(0, qualifyingSpend - base179);
    est168kDeduction = bonusBase * bonusDepreciationPct;

    const totalDeductible = est179Deduction + est168kDeduction;
    estTaxSavings = totalDeductible * taxRate;
  }

  const upfrontAfterTax = Math.max(0, upfrontGross - estTaxSavings);
  const upfrontNet = upfrontAfterTax;

  // Payback and net savings
  const paybackYears =
    annualSavings > 0 ? upfrontAfterTax / annualSavings : Infinity;
  const totalSavings = annualSavings * years;
  const netSavings = totalSavings - upfrontAfterTax;

  // Environmental impact
  const gallonsAvoidedAnnual = gallonsPerVehPerYr * fleetSize;
  const co2AnnualKgTailpipe = gallonsAvoidedAnnual * KG_CO2_PER_GAL_GAS;

  const kwhAnnualFleet = kwhPerVehPerYr * fleetSize;
  const gridEmissionsAnnual = kwhAnnualFleet * gridIntensity;
  const co2AnnualKgNet =
    co2AnnualKgTailpipe - (includeGridEmissions ? gridEmissionsAnnual : 0);

  const co2NetTons = (co2AnnualKgNet * years) / 1000;

  // Time series data for charts
  const costOverTimeIce: number[] = [];
  const costOverTimeEv: number[] = [];
  const cumulativeNetSavings: number[] = [];
  const yearsArray: number[] = [];

  let cumulative = -upfrontAfterTax;
  cumulativeNetSavings.push(cumulative);

  // Add Year 0 data (current costs without escalation)
  costOverTimeIce.push(Math.round(iceOpexAnnual)); 
  costOverTimeEv.push(Math.round(evOpexAnnual)); 
  yearsArray.push(0);

  for (let y = 1; y <= years; y++) {
    yearsArray.push(y);

    // Cost over time with escalation
    const gasPriceY = fuelPrice * Math.pow(1 + GAS_ESCALATION_YOY, y);
    const elecPriceY = elecPrice * Math.pow(1 + ELEC_ESCALATION_YOY, y);

    const iceOpexY =
      gallonsPerVehPerYr * gasPriceY * fleetSize + iceMaintAnnual;
    const evOpexY = kwhPerVehPerYr * elecPriceY * fleetSize + evMaintAnnual;

    costOverTimeIce.push(Math.round(iceOpexY));
    costOverTimeEv.push(Math.round(evOpexY));

    // Cumulative savings
    cumulative += annualSavings;
    cumulativeNetSavings.push(Math.round(cumulative));
  }

  return {
    // Location data
    address: formData.address,
    city: formData.city,
    zipCode: formData.zipCode,
    utilityRate: elecPrice,

    // Financial calculations
    iceFuelCostAnnual: Math.round(iceFuelCostAnnual),
    evEnergyCostAnnual: Math.round(evEnergyCostAnnual),
    iceMaintAnnual: Math.round(iceMaintAnnual),
    evMaintAnnual: Math.round(evMaintAnnual),
    iceOpexAnnual: Math.round(iceOpexAnnual),
    evOpexAnnual: Math.round(evOpexAnnual),
    annualSavings: Math.round(annualSavings),
    upfrontEVs: Math.round(upfrontEVs),
    incentives: Math.round(incentives),
    resaleTotal: Math.round(resaleTotal),
    upfrontGross: Math.round(upfrontGross),
    upfrontAfterTax: Math.round(upfrontAfterTax),
    upfrontNet: Math.round(upfrontNet),

    // Tax calculations
    applyTaxBenefits,
    businessTaxRate,
    section179Limit: Math.round(section179Limit),
    bonusDepreciationPct: bonusDepreciationPct * 100,
    qualifyingSpend: Math.round(qualifyingSpend),
    est179Deduction: Math.round(est179Deduction),
    est168kDeduction: Math.round(est168kDeduction),
    estTaxSavings: Math.round(estTaxSavings),

    // Long-term results
    paybackYears,
    totalSavings: Math.round(totalSavings),
    netSavings: Math.round(netSavings),

    // Environmental
    gallonsAvoidedAnnual: Math.round(gallonsAvoidedAnnual),
    co2AnnualKgTailpipe: Math.round(co2AnnualKgTailpipe),
    gridEmissionsAnnual: Math.round(gridEmissionsAnnual),
    co2AnnualKgNet: Math.round(co2AnnualKgNet),
    co2NetTons: parseFloat(co2NetTons.toFixed(2)),

    // Chart data
    costOverTimeIce,
    costOverTimeEv,
    cumulativeNetSavings,
    yearsArray: [0, ...yearsArray],
  };
};

// Formatting utilities
export const fmtMoney = (n: number): string =>
  !isFinite(n)
    ? "—"
    : n.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

export const fmtNum = (n: number, d: number = 0): string =>
  !isFinite(n)
    ? "—"
    : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
